import React from 'react';
import { BracketGame, Region, WinnerWithMetadata } from '../types/types';
import { GameBreakdown } from '../utils/pickBreakdownUtils';
import RegionBracket from './RegionBracket';
import FinalFourCenter from './FinalFourCenter';

interface TournamentBracketDesktopProps {
  games: BracketGame[];
  winnersWithMetadata: WinnerWithMetadata[];
  renderCompactGame: (game: BracketGame) => React.ReactNode;
  renderChampionshipGame?: (game: BracketGame) => React.ReactNode;
  setGames: React.Dispatch<React.SetStateAction<BracketGame[]>>;
  onBracketChange: (updatedGames: BracketGame[]) => void;
  championBreakdown?: GameBreakdown;
}

/**
 * Desktop 2x2 quadrant bracket layout:
 *   East  (LTR) ──┐               ┌── West  (RTL)
 *                 [FF 60] [CHAMP] [FF 61]
 *                  🏆 Champion 🏆
 *   South (LTR) ──┘               └── Midwest (RTL)
 */
const TournamentBracketDesktop: React.FC<TournamentBracketDesktopProps> = ({
  games,
  winnersWithMetadata,
  renderCompactGame,
  renderChampionshipGame,
  setGames,
  onBracketChange,
  championBreakdown,
}) => {
  return (
    <div className="overflow-x-auto">
      <div
        className="grid gap-x-0 gap-y-4 min-w-[1000px]"
        style={{
          gridTemplateColumns: '1fr auto 1fr',
          gridTemplateRows: '1fr 1fr',
        }}
      >
        {/* Row 1, Col 1: East (LTR) — feeds into FF 60 */}
        <div className="relative" style={{ gridRow: 1, gridColumn: 1 }}>
          <div className="absolute top-0 left-0 text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
            East
          </div>
          <div className="pt-5">
            <RegionBracket
              region={Region.EAST}
              games={games}
              direction="ltr"
              renderGame={renderCompactGame}
              showRoundLabels
            />
          </div>
        </div>

        {/* Center column spanning both rows: FF + Championship + Champion
            Negative margins let it overlap into the E8 area of adjacent brackets */}
        <div className="z-10 -mx-10 pointer-events-none" style={{ gridRow: '1 / 3', gridColumn: 2 }}>
          <FinalFourCenter
            games={games}
            winnersWithMetadata={winnersWithMetadata}
            renderGame={renderCompactGame}
            renderChampionshipGame={renderChampionshipGame}
            setGames={setGames}
            onBracketChange={onBracketChange}
            championBreakdown={championBreakdown}
          />
        </div>

        {/* Row 1, Col 3: West (RTL) — feeds into FF 61 */}
        <div className="relative" style={{ gridRow: 1, gridColumn: 3 }}>
          <div className="absolute top-0 right-0 text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
            West
          </div>
          <div className="pt-5">
            <RegionBracket
              region={Region.WEST}
              games={games}
              direction="rtl"
              renderGame={renderCompactGame}
              showRoundLabels
            />
          </div>
        </div>

        {/* Row 2, Col 1: South (LTR) — feeds into FF 60 */}
        <div className="relative" style={{ gridRow: 2, gridColumn: 1 }}>
          <div className="absolute top-0 left-0 text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
            South
          </div>
          <div className="pt-5">
            <RegionBracket
              region={Region.SOUTH}
              games={games}
              direction="ltr"
              renderGame={renderCompactGame}
            />
          </div>
        </div>

        {/* Row 2, Col 3: Midwest (RTL) — feeds into FF 61 */}
        <div className="relative" style={{ gridRow: 2, gridColumn: 3 }}>
          <div className="absolute top-0 right-0 text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
            Midwest
          </div>
          <div className="pt-5">
            <RegionBracket
              region={Region.MIDWEST}
              games={games}
              direction="rtl"
              renderGame={renderCompactGame}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentBracketDesktop;
